import React, { createContext, useContext, useState } from 'react';

/**
 * Local, non-redux UI state shared between `NoteNav` and `NoteViewer`: which note is
 * currently "active" (selected in the nav, shown in the viewer) and which note's source-document
 * modal (if any) is open. Deliberately plain `useState` -- no redux, no fetch -- both note KEYS
 * (`String(note.document_id)`) are derived from data already hydrated into `state.values['doc.notes']`
 * by the stateMapper; this context only tracks WHICH one is selected/open, never the note data
 * itself.
 *
 * `sourceModalNoteKey` is a SEPARATE field from `activeNoteKey` (not one field with a "modal is the
 * active note" convention) so closing the modal can restore prior nav/viewer state exactly (AC11):
 * `closeSourceModal` clears ONLY `sourceModalNoteKey`, leaving `activeNoteKey` (and therefore which
 * note the nav/viewer show) completely untouched.
 */
export interface NoteInteractionValue {
  readonly activeNoteKey: string | undefined;
  readonly setActiveNoteKey: (key: string) => void;
  readonly sourceModalNoteKey: string | undefined;
  readonly openSourceModal: (key: string) => void;
  readonly closeSourceModal: () => void;
}

const NoteInteractionContext = createContext<NoteInteractionValue | null>(null);

export const NoteInteractionProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [activeNoteKey, setActiveNoteKey] = useState<string | undefined>(
    undefined
  );
  const [sourceModalNoteKey, setSourceModalNoteKey] = useState<
    string | undefined
  >(undefined);

  const value: NoteInteractionValue = {
    activeNoteKey,
    setActiveNoteKey,
    sourceModalNoteKey,
    openSourceModal: setSourceModalNoteKey,
    closeSourceModal: () => setSourceModalNoteKey(undefined)
  };

  return (
    <NoteInteractionContext.Provider value={value}>
      {children}
    </NoteInteractionContext.Provider>
  );
};

/** Throws a clear, actionable error at the exact call site instead of silently working with
 *  `null` when a consumer renders outside `NoteInteractionProvider` -- mirrors
 *  `project_starburst/interface/context.tsx`'s `useCodingScreen` precedent. */
export function useNoteInteraction(): NoteInteractionValue {
  const ctx = useContext(NoteInteractionContext);
  if (!ctx) {
    throw new Error(
      'useNoteInteraction must be used within NoteInteractionProvider'
    );
  }
  return ctx;
}
